import React, { useState, useRef, useCallback } from "react";
import {
  Upload,
  FileSpreadsheet,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Undo2,
} from "lucide-react";
import { dataImportApi } from "../../services/api";
import PremiumModal from "./PremiumModal";
import SearchableSelect from "./SearchableSelect";

type EntityType = "clients" | "contacts";

interface EntityColumnMapping {
  [field: string]: number;
}

interface PreviewData {
  headers: string[];
  sampleRows: string[][];
  totalRows: number;
  mapping: EntityColumnMapping;
  entityType: EntityType;
}

interface ImportResult {
  batchId: string;
  imported: number;
  duplicates: number;
  skippedRows: number;
  errors: string[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
  defaultEntityType?: EntityType;
}

type Step = "upload" | "preview" | "success";

const CLIENT_FIELDS = [
  { value: "displayName", label: "Name *", required: true },
  { value: "firstName", label: "First Name" },
  { value: "lastName", label: "Last Name" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "type", label: "Type (individual/organization)" },
  { value: "tags", label: "Tags" },
  { value: "notes", label: "Notes" },
  { value: "address", label: "Address" },
  { value: "city", label: "City" },
  { value: "province", label: "Province / State" },
  { value: "postal", label: "Postal Code" },
];

const CONTACT_FIELDS = [
  { value: "displayName", label: "Name *", required: true },
  { value: "firstName", label: "First Name" },
  { value: "lastName", label: "Last Name" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "company", label: "Company" },
  { value: "role", label: "Role / Title" },
  { value: "category", label: "Category" },
  { value: "notes", label: "Notes" },
];

const DataImportModal: React.FC<Props> = ({
  open,
  onClose,
  onImported,
  defaultEntityType = "clients",
}) => {
  const [entityType, setEntityType] = useState<EntityType>(defaultEntityType);
  const [step, setStep] = useState<Step>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [mapping, setMapping] = useState<EntityColumnMapping | null>(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [undoing, setUndoing] = useState(false);
  const [undone, setUndone] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const fields = entityType === "clients" ? CLIENT_FIELDS : CONTACT_FIELDS;

  const reset = () => {
    setStep("upload");
    setFile(null);
    setPreview(null);
    setMapping(null);
    setSkipDuplicates(true);
    setLoading(false);
    setError("");
    setImportResult(null);
    setUndoing(false);
    setUndone(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = useCallback(
    async (selectedFile: File) => {
      setFile(selectedFile);
      setError("");
      setLoading(true);

      try {
        const formData = new FormData();
        formData.append("file", selectedFile);

        const previewFn =
          entityType === "clients"
            ? dataImportApi.previewClients
            : dataImportApi.previewContacts;

        const res = await previewFn(formData);
        const data = res.data as PreviewData;

        setPreview(data);
        setMapping(data.mapping);
        setStep("preview");
      } catch (err: any) {
        setError(err.response?.data?.error || "Failed to parse CSV file");
      } finally {
        setLoading(false);
      }
    },
    [entityType]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile) handleFile(droppedFile);
    },
    [handleFile]
  );

  const handleImport = async () => {
    if (!file || !mapping) return;
    setLoading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("mapping", JSON.stringify(mapping));
      formData.append("skipDuplicates", String(skipDuplicates));

      const importFn =
        entityType === "clients"
          ? dataImportApi.importClients
          : dataImportApi.importContacts;

      const res = await importFn(formData);
      setImportResult(res.data as ImportResult);
      setStep("success");
      onImported();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to import data");
    } finally {
      setLoading(false);
    }
  };

  const handleUndo = async () => {
    if (!importResult?.batchId) return;
    setUndoing(true);
    try {
      await dataImportApi.undoBatch(entityType, importResult.batchId);
      setUndone(true);
      onImported();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to undo import");
    } finally {
      setUndoing(false);
    }
  };

  const columnOptions = preview
    ? [
        { value: "-1", label: "(Skip)" },
        ...preview.headers.map((h, i) => ({
          value: String(i),
          label: `${i + 1}. ${h}`,
        })),
      ]
    : [];

  // Find which columns are mapped
  const mappedColumns = new Set(Object.values(mapping || {}).filter((v) => v >= 0));

  return (
    <PremiumModal
      open={open}
      onClose={handleClose}
      title={`Import ${entityType === "clients" ? "Clients" : "Contacts"}`}
      subtitle="Upload a CSV file to bulk import records"
      icon={FileSpreadsheet}
      size="lg"
      footer={
        <div className="flex justify-between items-center w-full">
          <div className="text-sm text-slate-500">
            {step === "preview" && preview && (
              <span>{preview.totalRows} rows detected</span>
            )}
          </div>
          <div className="flex gap-3">
            {step === "preview" && (
              <button
                onClick={reset}
                className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Back
              </button>
            )}
            {step === "preview" && (
              <button
                onClick={handleImport}
                disabled={loading || !mapping || mapping.displayName === undefined || mapping.displayName < 0}
                className="px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50"
                style={{ backgroundColor: "var(--color-primary)" }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "var(--color-primary)")
                }
              >
                {loading ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Importing...
                  </span>
                ) : (
                  `Import ${preview?.totalRows || 0} ${entityType === "clients" ? "Clients" : "Contacts"}`
                )}
              </button>
            )}
            {step === "success" && (
              <button
                onClick={handleClose}
                className="px-5 py-2.5 rounded-xl text-white text-sm font-bold"
                style={{ backgroundColor: "var(--color-primary)" }}
              >
                Done
              </button>
            )}
          </div>
        </div>
      }
    >
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm flex items-center gap-2">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Step 1: Upload */}
      {step === "upload" && (
        <div>
          {/* Entity type toggle */}
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1 mb-5 w-fit">
            {(["clients", "contacts"] as EntityType[]).map((et) => (
              <button
                key={et}
                onClick={() => setEntityType(et)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  entityType === et
                    ? "bg-white text-slate-800 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {et === "clients" ? "Clients" : "Contacts"}
              </button>
            ))}
          </div>

          <p className="text-sm text-slate-600 mb-4">
            Upload a CSV file with your{" "}
            {entityType === "clients" ? "client" : "contact"} data. Columns will
            be auto-detected.
          </p>

          <div
            className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors cursor-pointer ${
              dragging
                ? "border-brand-accent bg-brand-light"
                : "border-slate-300 hover:border-slate-400 bg-slate-50"
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            {loading ? (
              <div className="flex flex-col items-center">
                <Loader2 className="h-10 w-10 animate-spin text-emerald-600 mb-3" />
                <p className="text-sm font-medium text-slate-600">
                  Parsing CSV...
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <Upload className="h-10 w-10 text-slate-400 mb-3" />
                <p className="text-sm font-medium text-slate-700">
                  Drop your CSV file here or click to browse
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  CSV file up to 10 MB
                </p>
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
            }}
          />

          {/* Expected columns hint */}
          <div className="mt-4 p-3 bg-slate-50 rounded-lg">
            <p className="text-xs font-medium text-slate-500 mb-1">
              Expected columns:
            </p>
            <p className="text-xs text-slate-400">
              {fields.map((f) => f.label.replace(" *", "")).join(", ")}
            </p>
          </div>
        </div>
      )}

      {/* Step 2: Preview + Column Mapping */}
      {step === "preview" && preview && mapping && (
        <div>
          <p className="text-sm text-slate-600 mb-4">
            Review the detected column mapping and adjust if needed.
          </p>

          {/* Column Mapping */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
            {fields.map((field) => (
              <div key={field.value}>
                <label className="block text-xs text-slate-500 mb-1 font-medium">
                  {field.label}
                </label>
                <SearchableSelect
                  options={columnOptions}
                  value={
                    mapping[field.value] !== undefined
                      ? String(mapping[field.value])
                      : "-1"
                  }
                  onChange={(val) =>
                    setMapping({
                      ...mapping,
                      [field.value]: parseInt(val || "-1"),
                    })
                  }
                  placeholder="Select column"
                />
              </div>
            ))}
          </div>

          {/* Skip duplicates */}
          <label className="flex items-center gap-2 mb-4 cursor-pointer">
            <input
              type="checkbox"
              checked={skipDuplicates}
              onChange={(e) => setSkipDuplicates(e.target.checked)}
              className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
            />
            <span className="text-sm text-slate-600">
              Skip duplicates (same name or email)
            </span>
          </label>

          {/* Preview Table */}
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-xs">
              <thead>
                <tr
                  className="text-white"
                  style={{ backgroundColor: "var(--color-primary)" }}
                >
                  <th className="px-3 py-2 text-left font-semibold">#</th>
                  {preview.headers.map((h, i) => (
                    <th
                      key={i}
                      className="px-3 py-2 text-left font-semibold whitespace-nowrap"
                    >
                      {h}
                      {mappedColumns.has(i) && (
                        <span className="ml-1 text-emerald-200">
                          (
                          {fields.find(
                            (f) => mapping[f.value] === i
                          )?.label?.replace(" *", "") || "mapped"}
                          )
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {preview.sampleRows.slice(0, 5).map((row, ri) => (
                  <tr key={ri} className="hover:bg-slate-50">
                    <td className="px-3 py-2 text-slate-400">{ri + 1}</td>
                    {row.map((cell, ci) => (
                      <td
                        key={ci}
                        className={`px-3 py-2 whitespace-nowrap ${
                          mappedColumns.has(ci)
                            ? "text-slate-800 font-medium"
                            : "text-slate-500"
                        }`}
                      >
                        {cell || "-"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview.totalRows > 5 && (
            <p className="text-xs text-slate-400 mt-2">
              Showing first 5 of {preview.totalRows} rows
            </p>
          )}
        </div>
      )}

      {/* Step 3: Success */}
      {step === "success" && importResult && (
        <div className="text-center py-6">
          <CheckCircle2 className="h-16 w-16 mx-auto mb-4 text-emerald-500" />
          <h3 className="text-xl font-bold text-slate-800 mb-2">
            Import Complete
          </h3>
          <p className="text-sm text-slate-600 mb-1">
            Successfully imported{" "}
            <span className="font-bold text-emerald-700">
              {importResult.imported}
            </span>{" "}
            {entityType === "clients" ? "clients" : "contacts"}.
          </p>
          {importResult.duplicates > 0 && (
            <p className="text-sm text-amber-600 mb-1">
              {importResult.duplicates} duplicates skipped
            </p>
          )}
          {importResult.skippedRows > 0 && (
            <p className="text-sm text-amber-600 mb-1">
              {importResult.skippedRows} rows skipped (invalid data)
            </p>
          )}

          {importResult.errors.length > 0 && (
            <div className="mt-3 text-left bg-amber-50 rounded-lg p-3">
              <p className="text-xs font-medium text-amber-700 mb-1">
                Warnings:
              </p>
              {importResult.errors.slice(0, 5).map((err, i) => (
                <p key={i} className="text-xs text-amber-600">
                  {err}
                </p>
              ))}
              {importResult.errors.length > 5 && (
                <p className="text-xs text-amber-500 mt-1">
                  ...and {importResult.errors.length - 5} more
                </p>
              )}
            </div>
          )}

          {/* Undo button */}
          {!undone && importResult.imported > 0 && (
            <button
              onClick={handleUndo}
              disabled={undoing}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-200 text-red-600 text-sm font-medium hover:bg-red-50 transition-colors disabled:opacity-50"
            >
              {undoing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Undo2 className="h-4 w-4" />
              )}
              {undoing ? "Undoing..." : "Undo Import"}
            </button>
          )}
          {undone && (
            <p className="mt-3 text-sm text-emerald-600 font-medium">
              Import has been undone successfully.
            </p>
          )}
        </div>
      )}
    </PremiumModal>
  );
};

export default DataImportModal;
